import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useApi } from "../api/useApi";
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // User state
  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  });

  // Warning dialog state
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const { request } = useApi();
  // Timers
  const logoutTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);

  // Track last activity to throttle resets
  const lastActivityRef = useRef(Date.now());

  // ⏱ Configurable time limits
  const INACTIVITY_LIMIT = 5 * 60 * 1000; // 1 minute for testing
  const WARNING_TIME = 30 * 1000; // 30 seconds warning

  // Login function
  const login = (userData) => {
    setUser(userData);
    sessionStorage.setItem("user", JSON.stringify(userData));
    resetTimer();
  };

  // Logout function
  const logout = async (reason = "manual") => {
    try {
      const res = await request({
        url: `user/logout?email=${user.email}`,
        method: "GET",
      });
      setUser(null);
      sessionStorage.removeItem("user");
      clearAllTimers();
      navigate("/");
      if (reason === "timeout") {
        alert("You’ve been logged out due to inactivity.");
      }
    } catch (err) {
      console.error("Logout failed:", err);
    }

    // setUser(null);
    // sessionStorage.removeItem("user");
    // clearAllTimers();
    // navigate("/");
    // if (reason === "timeout") {
    //   alert("You’ve been logged out due to inactivity.");
    // }
  };

  // Clear all timers
  const clearAllTimers = () => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownInterval.current);
    setShowWarning(false);
    setTimeLeft(30);
  };

  // Reset timers
  const resetTimer = () => {
    clearAllTimers();

    // Schedule warning dialog
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      let remaining = 30;
      setTimeLeft(remaining);

      countdownInterval.current = setInterval(() => {
        remaining -= 1;
        setTimeLeft(remaining);
        if (remaining <= 0) clearInterval(countdownInterval.current);
      }, 1000);
    }, INACTIVITY_LIMIT - WARNING_TIME);

    // Schedule auto logout
    logoutTimer.current = setTimeout(() => {
      logout("timeout");
    }, INACTIVITY_LIMIT);

    console.log("Timers reset at", new Date().toLocaleTimeString());
  };

  // User activity tracking (throttled)
  useEffect(() => {
    if (!user) return;

    const events = [
      "mousemove",
      "mousedown",
      "click",
      "keypress",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      const now = Date.now();

      // ❌ Do NOT reset timers while warning is shown
      if (showWarning) return;

      if (now - lastActivityRef.current > 1000) {
        // throttle: once per second
        lastActivityRef.current = now;
        resetTimer();
      }
    };

    events.forEach((e) => window.addEventListener(e, handleActivity));
    resetTimer(); // start initially

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearAllTimers();
    };
  }, [user]); // ✅ remove showWarning from dependencies

  // Add this useEffect inside AuthProvider, after the existing activity tracking useEffect

// useEffect(() => {
//     console.log("sendBeacon useEffect triggered, user:", user); // ← add
//     if (!user) return;

//     const handleBeforeUnload = () => {
//         const url = `${process.env.REACT_APP_API_URL}/api/user/logout?email=${user.email}`;
//         console.log("Beacon firing:", url); // ← add
//         navigator.sendBeacon(url);
//     };

//     window.addEventListener('beforeunload', handleBeforeUnload);

//     return () => {
//         window.removeEventListener('beforeunload', handleBeforeUnload);
//     };
// }, [user]);

useEffect(() => {
    if (!user) return;

    // Track if user is navigating within the app
    let isInternalNavigation = false;

    const markInternal = () => {
        isInternalNavigation = true;
    };

   const handleBeforeUnload = () => {
    const url = `${process.env.REACT_APP_API_URL}/api/user/logout?email=${user.email}`;
    const blob = new Blob([], { type: 'text/plain' });
    navigator.sendBeacon(url, blob);
};

    // Mark all internal link clicks as internal navigation
    document.addEventListener('click', markInternal);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
        document.removeEventListener('click', markInternal);
        window.removeEventListener('beforeunload', handleBeforeUnload);
    };
}, [user]);

  // User chooses to stay logged in
  const stayLoggedIn = () => {
    setShowWarning(false);
    resetTimer();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}

      {/* Session Timeout Warning Dialog */}
      <Dialog
        open={showWarning}
        onClose={() => {}}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle>Session Timeout Warning</DialogTitle>
        <DialogContent>
          <Typography>
            You have been inactive for a while. You will be logged out
            automatically in <strong>{timeLeft}</strong> seconds.
          </Typography>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
